<template>
  <div class="table-container">
    <el-table :data="data" v-loading="loading" style="width: 100%" border>
      <el-table-column prop="id" :label="$t('MemberRegistrationReview.serialNumber')" width="80" align="center"/>
      
      <el-table-column prop="contactName" :label="$t('MemberRegistrationReview.contactName')" width="120" align="center"/>
      
      <el-table-column prop="storeName" :label="$t('MemberRegistrationReview.storeName')" min-width="150" show-overflow-tooltip/>
      
      <el-table-column prop="email" :label="$t('MemberRegistrationReview.email')" min-width="160" show-overflow-tooltip/>
      
      <el-table-column prop="tel" :label="$t('MemberRegistrationReview.tel')" width="130" align="center"/>
      
      <el-table-column :label="$t('MemberRegistrationReview.country')" width="100" align="center">
        <template #default="scope">
          {{ getCountryName(scope.row.country) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="applicantStatus" :label="$t('MemberRegistrationReview.applicantStatus')" width="120" align="center">
        <template #default="scope">
          <el-tag :type="getStatusType(scope.row.applicantStatus)">
            {{ scope.row.statusText }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="applyTime" :label="$t('MemberRegistrationReview.applyTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.applyTime) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewer" :label="$t('MemberRegistrationReview.reviewer')" width="120" align="center">
        <template #default="scope">
          {{ scope.row.reviewer || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewTime" :label="$t('MemberRegistrationReview.reviewTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.reviewTime) || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewComment" :label="$t('MemberRegistrationReview.reviewComment')" min-width="150" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.reviewComment || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('MemberRegistrationReview.operation')" width="200" align="center">
        <template #default="scope">
          <div v-if="Number(scope.row.applicantStatus) === 0" class="action-buttons">
            <el-button 
              @click="handleApprove(scope.row)" 
              type="success" 
              size="small"
              :loading="scope.row.approving"
              class="approve-btn">
              <el-icon><Check /></el-icon>
              {{ $t('MemberRegistrationReview.approve') }}
            </el-button>
            <el-button 
              @click="handleReject(scope.row)" 
              type="danger" 
              size="small"
              :loading="scope.row.rejecting"
              class="reject-btn">
              <el-icon><Close /></el-icon>
              {{ $t('MemberRegistrationReview.reject') }}
            </el-button>
          </div>
          
          <div v-else class="status-display">
            <el-tag 
              :type="Number(scope.row.applicantStatus) === 1 ? 'success' : 'danger'" 
              size="small"
              class="status-tag">
              {{ Number(scope.row.applicantStatus) === 1 ? $t('MemberRegistrationReview.statusApproved') : $t('MemberRegistrationReview.statusRejected') }}
            </el-tag>
            <el-button 
              @click="handleView(scope.row)" 
              type="info" 
              size="small"
              class="view-btn">
              <el-icon><View /></el-icon>
              {{ $t('MemberRegistrationReview.view') }}
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :model-value="reviewDialogVisible" :title="getDialogTitle()" width="900px" :show-close="true" @close="closeReviewDialog">
      <div v-loading="reviewLoading">
      <el-form :model="reviewForm" :rules="reviewRules" ref="reviewFormRef" label-width="180px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.contactName')">
              <el-input v-model="currentApplication.contactName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.storeName')">
              <el-input v-model="currentApplication.storeName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.email')">
              <el-input v-model="currentApplication.email" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.tel')">
              <el-input v-model="currentApplication.tel" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.country')">
              <el-input :value="getCountryName(currentApplication.country)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.location')">
              <el-input v-model="currentApplication.location" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.postCode')">
              <el-input v-model="currentApplication.postCode" disabled />
              <span v-if="!postCodeValidation.valid" style="color: #F56C6C; font-size: 12px; margin-top: 4px; display: block;">{{ postCodeValidation.message }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.taxNumber')">
              <el-input v-model="currentApplication.taxNumber" disabled />
              <div style="margin-top: 8px; display: flex; flex-direction: column; gap: 4px;">
                <a href="https://ec.europa.eu/taxation_customs/vies/#/vat-validation" target="_blank" style="font-size: 12px; color: #409EFF; text-decoration: none;">{{ $t('MemberRegistrationReview.vatValidationEu') }}</a>
                <a href="https://vatapp.net/vat-number-check" target="_blank" style="font-size: 12px; color: #409EFF; text-decoration: none;">{{ $t('MemberRegistrationReview.vatValidationNonEu') }}</a>
                <a href="https://www.tax.service.gov.uk/check-vat-number/enter-vat-details" target="_blank" style="font-size: 12px; color: #409EFF; text-decoration: none;">{{ $t('MemberRegistrationReview.vatValidationUk') }}</a>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.website')">
              <el-input v-model="currentApplication.website" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.businessLicense')">
              <div v-if="currentApplication.businessLicense" class="license-files">
                <el-link 
                  v-for="(license, index) in getBusinessLicenseList(currentApplication.businessLicense)" 
                  :key="index"
                  @click="previewBusinessLicense(license)" 
                  type="primary" 
                  :underline="false" 
                  style="cursor: pointer; display: block; margin-bottom: 4px;"
                >
                  <span style="margin-right: 8px;">{{ index + 1 }}.</span>
                  {{ getBusinessLicenseFileName(license) }}
                </el-link>
              </div>
              <span v-else>-</span>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.workWithAnanda')">
              <el-input :value="getWorkWithAnandaText(currentApplication)" disabled />
            </el-form-item>
          </el-col>
          <template v-if="getWorkWithAnandaText(currentApplication) === $t('MemberRegistrationReview.yes')">
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.howKnowAnanda')">
                <el-input :value="getHowKnowText(currentApplication.howKnowAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.whyChooseAnanda')">
                <el-input :value="getWhyChooseText(currentApplication.whyChooseAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.additionalInfo')">
                <el-input v-model="currentApplication.additionalInfo" type="textarea" :rows="3" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.subscribeNewsletter')">
                <el-input :value="currentApplication.subscribeNewsletter === true || currentApplication.subscribeNewsletter === 'yes' ? $t('MemberRegistrationReview.yes') : $t('MemberRegistrationReview.no')" disabled />
              </el-form-item>
            </el-col>
          </template>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.officeArea')">
              <el-input :value="getAreaText(currentApplication.officeArea)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.warehouse')">
              <el-input :value="getAreaText(currentApplication.warehouse)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.staff')">
              <el-input :value="getStaffText(currentApplication.staff)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.serviceCapability')">
              <el-input :value="getServiceCapabilitiesText(currentApplication.serviceCapability || currentApplication.service_capability)" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.remark')">
              <el-input :value="currentApplication.remark || currentApplication.note || ''" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.applicantStatus')">
              <el-input v-model="currentApplication.statusText" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.applyTime')">
              <el-input :value="formatDateTime(currentApplication.applyTime)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.reviewer')">
              <el-input v-model="currentApplication.reviewer" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.reviewTime')">
              <el-input :value="currentApplication.reviewTime ? formatDateTime(currentApplication.reviewTime) : '-'" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item :label="$t('MemberRegistrationReview.reviewResult')" prop="reviewResult">
          <el-select v-model="reviewForm.reviewResult" style="width: 100%" :disabled="reviewForm.reviewResult !== ''">
            <el-option :label="$t('MemberRegistrationReview.statusApproved')" value="approved" />
            <el-option :label="$t('MemberRegistrationReview.statusRejected')" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('MemberRegistrationReview.reviewComment')" prop="reviewComment">
          <el-input 
            v-model="reviewForm.reviewComment" 
            type="textarea" 
            :rows="4" 
            :placeholder="$t('MemberRegistrationReview.reviewCommentPlaceholder')"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      </div>
      <template #footer>
        <el-button @click="closeReviewDialog" type="default">{{ $t('MemberRegistrationReview.cancel') }}</el-button>
        <el-button @click="submitReview" :loading="reviewLoading" type="primary">{{ $t('MemberRegistrationReview.confirmReview') }}</el-button>
      </template>
    </el-dialog>

    <el-dialog :model-value="viewDialogVisible" :title="$t('MemberRegistrationReview.viewApplication')" width="900px" :show-close="true" @close="closeViewDialog">
      <div v-loading="viewLoading">
      <el-form :model="currentApplication" label-width="180px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.contactName')">
              <el-input v-model="currentApplication.contactName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.storeName')">
              <el-input v-model="currentApplication.storeName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.email')">
              <el-input v-model="currentApplication.email" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.tel')">
              <el-input v-model="currentApplication.tel" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.country')">
              <el-input :value="getCountryName(currentApplication.country)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.location')">
              <el-input v-model="currentApplication.location" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.postCode')">
              <el-input v-model="currentApplication.postCode" disabled />
              <span v-if="!postCodeValidation.valid" style="color: #F56C6C; font-size: 12px; margin-top: 4px; display: block;">{{ postCodeValidation.message }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.taxNumber')">
              <el-input v-model="currentApplication.taxNumber" disabled />
              <div style="margin-top: 8px; display: flex; flex-direction: column; gap: 4px;">
                <a href="https://ec.europa.eu/taxation_customs/vies/#/vat-validation" target="_blank" style="font-size: 12px; color: #409EFF; text-decoration: none;">{{ $t('MemberRegistrationReview.vatValidationEu') }}</a>
                <a href="https://vatapp.net/vat-number-check" target="_blank" style="font-size: 12px; color: #409EFF; text-decoration: none;">{{ $t('MemberRegistrationReview.vatValidationNonEu') }}</a>
                <a href="https://www.tax.service.gov.uk/check-vat-number/enter-vat-details" target="_blank" style="font-size: 12px; color: #409EFF; text-decoration: none;">{{ $t('MemberRegistrationReview.vatValidationUk') }}</a>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.website')">
              <el-input v-model="currentApplication.website" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.businessLicense')">
              <div v-if="currentApplication.businessLicense" class="license-files">
                <el-link 
                  v-for="(license, index) in getBusinessLicenseList(currentApplication.businessLicense)" 
                  :key="index"
                  @click="previewBusinessLicense(license)" 
                  type="primary" 
                  :underline="false" 
                  style="cursor: pointer; display: block; margin-bottom: 4px;"
                >
                  <span style="margin-right: 8px;">{{ index + 1 }}.</span>
                  {{ getBusinessLicenseFileName(license) }}
                </el-link>
              </div>
              <span v-else>-</span>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.workWithAnanda')">
              <el-input :value="getWorkWithAnandaText(currentApplication)" disabled />
            </el-form-item>
          </el-col>
          <template v-if="getWorkWithAnandaText(currentApplication) === $t('MemberRegistrationReview.yes')">
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.howKnowAnanda')">
                <el-input :value="getHowKnowText(currentApplication.howKnowAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.whyChooseAnanda')">
                <el-input :value="getWhyChooseText(currentApplication.whyChooseAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.additionalInfo')">
                <el-input v-model="currentApplication.additionalInfo" type="textarea" :rows="3" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('MemberRegistrationReview.subscribeNewsletter')">
                <el-input :value="currentApplication.subscribeNewsletter === 'yes' ? $t('MemberRegistrationReview.yes') : $t('MemberRegistrationReview.no')" disabled />
              </el-form-item>
            </el-col>
          </template>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.officeArea')">
              <el-input :value="getAreaText(currentApplication.officeArea)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.warehouse')">
              <el-input :value="getAreaText(currentApplication.warehouse)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.staff')">
              <el-input :value="getStaffText(currentApplication.staff)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.serviceCapability')">
              <el-input :value="getServiceCapabilitiesText(currentApplication.serviceCapability || currentApplication.service_capability)" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.remark')">
              <el-input v-model="currentApplication.remark" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.applicantStatus')">
              <el-input v-model="currentApplication.statusText" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.applyTime')">
              <el-input :value="formatDateTime(currentApplication.applyTime)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.reviewer')">
              <el-input v-model="currentApplication.reviewer" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('MemberRegistrationReview.reviewTime')">
              <el-input :value="currentApplication.reviewTime ? formatDateTime(currentApplication.reviewTime) : '-'" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('MemberRegistrationReview.reviewComment')">
              <el-input v-model="currentApplication.reviewComment" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      </div>
      <template #footer>
        <el-button @click="closeViewDialog" type="default">{{ $t('MemberRegistrationReview.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import dialogMixin from '@/mixins/dialogMixin';
import countryMixin from '@/mixins/countryMixin';
import { passMemberApply, rejectMemberApply, getMemberApplyDetail, getSignUrl } from '@/api/api';

export default {
  mixins: [dialogMixin, countryMixin],
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      reviewDialogVisible: false,
      viewDialogVisible: false,
      currentApplication: {},
      reviewForm: {
        reviewResult: '',
        reviewComment: ''
      },
      reviewRules: {
        reviewResult: [
          { required: true, message: this.$t('MemberRegistrationReview.reviewResultRequired'), trigger: 'change' }
        ],
        reviewComment: [
          { required: true, message: this.$t('MemberRegistrationReview.reviewCommentRequired'), trigger: 'blur' },
          { min: 5, message: this.$t('MemberRegistrationReview.reviewCommentMinLength'), trigger: 'blur' }
        ]
      },
      reviewLoading: false,
      viewLoading: false,
      postCodeValidation: { valid: true, message: '' }
    };
  },
  mounted() {
    this.fetchCountryOptions();
  },
  methods: {
    getStatusType(status) {
      const numericStatus = Number(status);
      const typeMap = {
        0: 'warning',
        1: 'success',
        2: 'danger',
        'pending': 'warning',
        'approved': 'success',
        'rejected': 'danger'
      };
      return typeMap[numericStatus] || typeMap[status] || 'info';
    },

    getStatusText(status) {
      const numericStatus = Number(status);
      const textMap = {
        0: this.$t('MemberRegistrationReview.statusPending'),
        1: this.$t('MemberRegistrationReview.statusApproved'),
        2: this.$t('MemberRegistrationReview.statusRejected'),
        'pending': this.$t('MemberRegistrationReview.statusPending'),
        'approved': this.$t('MemberRegistrationReview.statusApproved'),
        'rejected': this.$t('MemberRegistrationReview.statusRejected')
      };
      return textMap[numericStatus] || textMap[status] || status || '';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    getCountryName(countryCode) {
      if (!countryCode) return '-';
      const country = this.countryOptions.find(c => c.code === countryCode);
      return country ? country.label : countryCode;
    },

    getAreaText(value) {
      const areaMap = {
        'less20': '< 20m²',
        '20-50': '20-50m²',
        '50-100': '50-100m²',
        'more100': '> 100m²',
        'warehouse_less50': '< 50m²',
        'warehouse_50-100': '50-100m²',
        'warehouse_100-200': '100-200m²',
        'warehouse_more200': '> 200m²',
        'warehouse_none': '无仓库'
      };
      return areaMap[value] || value || '-';
    },

    getStaffText(value) {
      const staffMap = {
        'staff_less5': '< 5 persons',
        'staff_5-10': '5-10 persons',
        'staff_10-20': '10-20 persons',
        'staff_more20': '> 20 persons'
      };
      return staffMap[value] || value || '-';
    },

    getServiceCapabilitiesText(capabilities) {
      if (!capabilities) return '-';
      const capArray = Array.isArray(capabilities) ? capabilities : capabilities.split(',');
      const capMap = {
        'hotLine': this.$t('MemberRegistration.serviceHotLine'),
        'service5d8h': this.$t('MemberRegistration.service5d8h'),
        'bikeRepair': this.$t('MemberRegistration.bikeRepair'),
        'electricalRepair': this.$t('MemberRegistration.electricalRepair'),
        'spareParts': this.$t('MemberRegistration.spareParts'),
        'onSiteService': this.$t('MemberRegistration.onSiteService')
      };
      return capArray.map(c => capMap[c] || c).join(', ');
    },

    getBusinessLicenseFileName(key) {
      if (!key) return '-';
      return key.split('/').pop();
    },

    async previewBusinessLicense(key) {
      if (!key) return;
      try {
        const result = await getSignUrl(key);
        let signedUrl = '';
        if (typeof result === 'string') {
          signedUrl = result;
        } else if (result && (result.url || result.data?.url)) {
          signedUrl = result.url || result.data?.url;
        }
        if (signedUrl && signedUrl.startsWith('http')) {
          window.open(signedUrl, '_blank');
        } else {
          ElMessage.error(this.$t('MemberRegistrationReview.previewFailed'));
        }
      } catch (error) {
        console.error('获取文件预览链接失败:', error);
        ElMessage.error(this.$t('MemberRegistrationReview.previewFailed'));
      }
    },

    getBusinessLicenseList(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return value.split(',').filter(v => v.trim());
    },

    getWorkWithAnandaText(item) {
      if (item.workWithAnanda === 'yes') return this.$t('MemberRegistrationReview.yes');
      if (item.workWithAnanda === 'no') return this.$t('MemberRegistrationReview.no');
      if (item.isUseAnanda === 1 || item.is_use_ananda === 1) return this.$t('MemberRegistrationReview.yes');
      return this.$t('MemberRegistrationReview.no');
    },

    getHowKnowText(value) {
      if (!value) return '-';
      const valArray = Array.isArray(value) ? value : value.split(',');
      const map = {
        'exhibition': this.$t('MemberRegistration.exhibition'),
        'magazines': this.$t('MemberRegistration.magazines'),
        'socialMedia': this.$t('MemberRegistration.socialMedia'),
        'supplier': this.$t('MemberRegistration.supplier')
      };
      return valArray.map(v => map[v] || v).join(', ');
    },

    getWhyChooseText(value) {
      if (!value) return '-';
      const valArray = Array.isArray(value) ? value : value.split(',');
      const map = {
        'qualityPrice': this.$t('MemberRegistration.qualityPrice'),
        'differentiation': this.$t('MemberRegistration.differentiation'),
        'supplierAdvice': this.$t('MemberRegistration.supplierAdvice'),
        'other': this.$t('MemberRegistration.other')
      };
      return valArray.map(v => map[v] || v).join(', ');
    },

    getDialogTitle() {
      if (this.reviewForm.reviewResult === 'approved') {
        return this.$t('MemberRegistrationReview.approveApplication');
      } else if (this.reviewForm.reviewResult === 'rejected') {
        return this.$t('MemberRegistrationReview.rejectApplication');
      }
      return this.$t('MemberRegistrationReview.reviewApplication');
    },

    async handleApprove(row) {
      this.reviewLoading = true;
      try {
        const response = await getMemberApplyDetail(row.id);
        const detailData = response?.data || response;
        const auditStatus = detailData.audit_status !== undefined ? detailData.audit_status : (detailData.applicantStatus || detailData.status);
        const countryCode = detailData.country || detailData.country_code || row.country || '';
        this.currentApplication = {
          ...row,
          ...detailData,
          contactName: detailData.contactName || detailData.contact_name || row.contactName || '',
          storeName: detailData.storeName || detailData.store_name || row.storeName || '',
          email: detailData.email || row.email || '',
          tel: detailData.tel || detailData.phone || row.tel || '',
          country: countryCode,
          location: detailData.location || detailData.address || row.location || '',
          postCode: detailData.postCode || detailData.post_code || row.postCode || '',
          taxNumber: detailData.taxNumber || detailData.tax_number || row.taxNumber || '',
          officeArea: detailData.officeArea || detailData.office_area || row.officeArea || '',
          warehouse: detailData.warehouse || detailData.warehouse_area || row.warehouse || '',
          staff: detailData.staff || detailData.staff_count || row.staff || '',
          workWithAnanda: detailData.workWithAnanda || ((detailData.isUseAnanda == 1 || detailData.is_use_ananda == 1) ? 'yes' : 'no') || row.workWithAnanda,
          howKnowAnanda: detailData.howKnowAnanda || detailData.know_channel || row.howKnowAnanda || '',
          whyChooseAnanda: detailData.whyChooseAnanda || detailData.cooperate_reason || row.whyChooseAnanda || '',
          applicantStatus: auditStatus,
          statusText: this.getStatusText(auditStatus),
          applyTime: detailData.applyTime || detailData.apply_time || row.applyTime || '',
          reviewer: detailData.reviewer || detailData.audit_user_name || localStorage.getItem('userName') || '',
          reviewTime: detailData.reviewTime || detailData.audit_time || row.reviewTime || '',
          website: detailData.website || row.website || '',
          businessLicense: detailData.businessLicense || detailData.business_license || row.businessLicense || '',
          subscribeNewsletter: detailData.subscribeNewsletter || detailData.subscribe_newsletter || row.subscribeNewsletter || '',
          serviceCapability: detailData.serviceCapability || detailData.service_capability || row.serviceCapability || '',
          additionalInfo: detailData.additionalInfo || detailData.additional_info || row.additionalInfo || '',
          remark: detailData.remark || detailData.note || row.remark || '',
          reviewComment: detailData.reviewComment || detailData.audit_comment || row.reviewComment || ''
        };
        if (!this.countriesLoaded) {
          await this.fetchCountryOptions();
        }
        this.postCodeValidation = this.validatePostalCode(this.currentApplication.country, this.currentApplication.postCode);
      } catch (error) {
        console.error('获取详情失败:', error);
        this.currentApplication = { ...row };
        this.postCodeValidation = { valid: true, message: '' };
        ElMessage.error(this.$t('MemberRegistrationReview.reviewFailed'));
      } finally {
        this.reviewLoading = false;
      }
      this.reviewForm = {
        reviewResult: 'approved',
        reviewComment: ''
      };
      this.reviewDialogVisible = true;
    },

    async handleReject(row) {
      this.reviewLoading = true;
      try {
        const response = await getMemberApplyDetail(row.id);
        const detailData = response?.data || response;
        const auditStatus = detailData.audit_status !== undefined ? detailData.audit_status : (detailData.applicantStatus || detailData.status);
        const countryCode = detailData.country || detailData.country_code || row.country || '';
        this.currentApplication = {
          ...row,
          ...detailData,
          contactName: detailData.contactName || detailData.contact_name || row.contactName || '',
          storeName: detailData.storeName || detailData.store_name || row.storeName || '',
          email: detailData.email || row.email || '',
          tel: detailData.tel || detailData.phone || row.tel || '',
          country: countryCode,
          location: detailData.location || detailData.address || row.location || '',
          postCode: detailData.postCode || detailData.post_code || row.postCode || '',
          taxNumber: detailData.taxNumber || detailData.tax_number || row.taxNumber || '',
          officeArea: detailData.officeArea || detailData.office_area || row.officeArea || '',
          warehouse: detailData.warehouse || detailData.warehouse_area || row.warehouse || '',
          staff: detailData.staff || detailData.staff_count || row.staff || '',
          workWithAnanda: detailData.workWithAnanda || ((detailData.isUseAnanda == 1 || detailData.is_use_ananda == 1) ? 'yes' : 'no') || row.workWithAnanda,
          howKnowAnanda: detailData.howKnowAnanda || detailData.know_channel || row.howKnowAnanda || '',
          whyChooseAnanda: detailData.whyChooseAnanda || detailData.cooperate_reason || row.whyChooseAnanda || '',
          applicantStatus: auditStatus,
          statusText: this.getStatusText(auditStatus),
          applyTime: detailData.applyTime || detailData.apply_time || row.applyTime || '',
          reviewer: detailData.reviewer || detailData.audit_user_name || localStorage.getItem('userName') || '',
          reviewTime: detailData.reviewTime || detailData.audit_time || row.reviewTime || '',
          website: detailData.website || row.website || '',
          businessLicense: detailData.businessLicense || detailData.business_license || row.businessLicense || '',
          subscribeNewsletter: detailData.subscribeNewsletter || detailData.subscribe_newsletter || row.subscribeNewsletter || '',
          serviceCapability: detailData.serviceCapability || detailData.service_capability || row.serviceCapability || '',
          additionalInfo: detailData.additionalInfo || detailData.additional_info || row.additionalInfo || '',
          remark: detailData.remark || detailData.note || row.remark || '',
          reviewComment: detailData.reviewComment || detailData.audit_comment || row.reviewComment || ''
        };
        if (!this.countriesLoaded) {
          await this.fetchCountryOptions();
        }
        this.postCodeValidation = this.validatePostalCode(this.currentApplication.country, this.currentApplication.postCode);
      } catch (error) {
        console.error('获取详情失败:', error);
        this.currentApplication = { ...row };
        this.postCodeValidation = { valid: true, message: '' };
        ElMessage.error(this.$t('MemberRegistrationReview.reviewFailed'));
      } finally {
        this.reviewLoading = false;
      }
      this.reviewForm = {
        reviewResult: 'rejected',
        reviewComment: ''
      };
      this.reviewDialogVisible = true;
    },

    async handleView(row) {
      this.viewDialogVisible = true;
      this.viewLoading = true;
      try {
        const response = await getMemberApplyDetail(row.id);
        const detailData = response?.data || response;
        const auditStatus = detailData.audit_status !== undefined ? detailData.audit_status : (detailData.applicantStatus || detailData.status);
        const countryCode = detailData.country || detailData.country_code || row.country || '';
        this.currentApplication = {
          ...row,
          ...detailData,
          contactName: detailData.contactName || detailData.contact_name || row.contactName || '',
          storeName: detailData.storeName || detailData.store_name || row.storeName || '',
          country: countryCode,
          email: detailData.email || row.email || '',
          tel: detailData.tel || detailData.phone || row.tel || '',
          location: detailData.location || detailData.address || row.location || '',
          postCode: detailData.postCode || detailData.post_code || row.postCode || '',
          taxNumber: detailData.taxNumber || detailData.tax_number || row.taxNumber || '',
          officeArea: detailData.officeArea || detailData.office_area || row.officeArea || '',
          warehouse: detailData.warehouse || detailData.warehouse_area || row.warehouse || '',
          staff: detailData.staff || detailData.staff_count || row.staff || '',
          workWithAnanda: detailData.workWithAnanda || ((detailData.isUseAnanda == 1 || detailData.is_use_ananda == 1) ? 'yes' : 'no') || row.workWithAnanda,
          howKnowAnanda: detailData.howKnowAnanda || detailData.know_channel || row.howKnowAnanda || '',
          whyChooseAnanda: detailData.whyChooseAnanda || detailData.cooperate_reason || row.whyChooseAnanda || '',
          applicantStatus: auditStatus,
          statusText: this.getStatusText(auditStatus),
          applyTime: detailData.applyTime || detailData.apply_time || row.applyTime || '',
          reviewer: detailData.reviewer || detailData.audit_user_name || localStorage.getItem('userName') || '',
          reviewTime: detailData.reviewTime || detailData.audit_time || row.reviewTime || '',
          website: detailData.website || row.website || '',
          businessLicense: detailData.businessLicense || detailData.business_license || row.businessLicense || '',
          subscribeNewsletter: detailData.subscribeNewsletter || detailData.subscribe_newsletter || row.subscribeNewsletter || '',
          serviceCapability: detailData.serviceCapability || detailData.service_capability || row.serviceCapability || '',
          additionalInfo: detailData.additionalInfo || detailData.additional_info || row.additionalInfo || '',
          remark: detailData.remark || detailData.note || row.remark || '',
          reviewComment: detailData.reviewComment || detailData.audit_comment || row.reviewComment || ''
        };
        if (!this.countriesLoaded) {
          await this.fetchCountryOptions();
        }
        this.postCodeValidation = this.validatePostalCode(this.currentApplication.country, this.currentApplication.postCode);
      } catch (error) {
        console.error('获取详情失败:', error);
        this.currentApplication = { ...row };
        this.postCodeValidation = { valid: true, message: '' };
        ElMessage.error(this.$t('MemberRegistrationReview.reviewFailed'));
      } finally {
        this.viewLoading = false;
      }
    },

    closeReviewDialog() {
      this.reviewDialogVisible = false;
      this.currentApplication = {};
      this.reviewForm = {
        reviewResult: '',
        reviewComment: ''
      };
    },

    closeViewDialog() {
      this.viewDialogVisible = false;
      this.currentApplication = {};
    },

    async submitReview() {
      try {
        if (!this.$refs.reviewFormRef) {
          ElMessage.error(this.$t('MemberRegistrationReview.formValidationError'));
          return;
        }
        
        await this.$refs.reviewFormRef.validate();
        
        this.reviewLoading = true;
        
        const applyId = this.currentApplication.id;
        const reviewComment = this.reviewForm.reviewComment;
        const reviewerName = localStorage.getItem('userName') || '';
        
        let response;
        if (this.reviewForm.reviewResult === 'approved') {
          response = await passMemberApply(applyId, { auditComment: reviewComment, reviewer: reviewerName });
        } else {
          response = await rejectMemberApply(applyId, { auditComment: reviewComment, reviewer: reviewerName });
        }
        
        const msg = response?.message || response?.msg;
        if (response?.code === -1) {
          ElMessage.error(msg || this.$t('MemberRegistrationReview.reviewFailed'));
        } else {
          ElMessage.success(msg || this.$t('MemberRegistrationReview.reviewSuccess'));
          this.closeReviewDialog();
          this.$emit('refresh');
        }
        
      } catch (error) {
        if (error !== false) {
          console.error('审核失败:', error);
          if (!(error && error.message === this.$t('MemberRegistrationReview.reviewCommentMinLength'))) {
            const status = error?.response?.status;
            if (status === 500) {
              ElMessage.error(this.$t('MemberRegistrationReview.reviewFailed'));
            } else {
              const errorMsg = error?.response?.data?.message || error?.response?.data?.msg || error?.message || this.$t('MemberRegistrationReview.reviewFailed');
              ElMessage.error(errorMsg);
            }
          }
        }
      } finally {
        this.reviewLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  margin: 20px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.approve-btn {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.3);
  transition: all 0.3s ease;
}

.approve-btn:hover {
  background: linear-gradient(135deg, #5daf34 0%, #7bc85a 100%);
  box-shadow: 0 4px 8px rgba(103, 194, 58, 0.4);
  transform: translateY(-1px);
}

.reject-btn {
  background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.3);
  transition: all 0.3s ease;
}

.reject-btn:hover {
  background: linear-gradient(135deg, #f45656 0%, #f77777 100%);
  box-shadow: 0 4px 8px rgba(245, 108, 108, 0.4);
  transform: translateY(-1px);
}

.status-display {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.status-tag {
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
}

.view-btn {
  background: linear-gradient(135deg, #909399 0%, #a6a9ad 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(144, 147, 153, 0.3);
  transition: all 0.3s ease;
}

.view-btn:hover {
  background: linear-gradient(135deg, #82848a 0%, #9ca0a4 100%);
  box-shadow: 0 4px 8px rgba(144, 147, 153, 0.4);
  transform: translateY(-1px);
}

.el-button {
  border-radius: 6px;
  font-weight: 500;
}

.el-button .el-icon {
  margin-right: 4px;
}
</style>